import { LOCALE as LOCALE_ORIGINAL } from './constants';
import toStringFactory from './toStringFactory';
import { typeOfV2 } from './typeOfV2';

export function toHumanizedCriteria(criteria, options = {}) {
  const { v1, op, v2 } = options;
  const toStringV1 = _toHumanizedV1.bind(null, options);
  const toStringOp = _toHumanizedOp.bind(null, options);
  const toStringV2 = _toHumanizedV2.bind(null, options);
  return `${v1 ? v1(criteria, toStringV1) : toStringV1(criteria)} ${
    op ? op(criteria, toStringOp) : toStringOp(criteria)
  } ${v2 ? v2(criteria, toStringV2) : toStringV2(criteria)}`;
}

export function toHumanizedV1(criteria, options = {}) {
  const { v1, op, v2 } = options;
  const toStringV1 = _toHumanizedV1.bind(null, options);
  return v2 ? v2(criteria, toStringV1) : toStringV1(criteria);
}

export function toHumanizedOp(criteria, options = {}) {
  const { v1, op, v2 } = options;
  const toStringOp = _toHumanizedOp.bind(null, options);
  return v2 ? v2(criteria, toStringOp) : toStringOp(criteria);
}

export function toHumanizedV2(criteria, options = {}) {
  const { v1, op, v2 } = options;
  const toStringV2 = _toHumanizedV2.bind(null, options);
  return v2 ? v2(criteria, toStringV2) : toStringV2(criteria);
}

/**
 * @param {object} filter - Objeto {name: description} para mapear los nombres de campo
 * @param {object} options.fields - Objeto {name: description} para mapear los nombres de campo
 * @param {object} options.LOCALE - Objeto con las traducciones de las operaciones, y otros
 */

// export function _toHumanized(filter, { fields = {}, LOCALE = LOCALE_ORIGINAL, v1, op, v2 }={}) {
export function toHumanized(filter, options = {}) {
  const { v1, op, v2 } = options;
  const toStringV1 = _toHumanizedV1.bind(null, options);
  const toStringOp = _toHumanizedOp.bind(null, options);
  const toStringV2 = _toHumanizedV2.bind(null, options);
  const toStringUnion = _toHumanizedUnion.bind(null, options);
  const toStringOrderBy = _toHumanizedOrderBy.bind(null, options);
  const toStringGroupBy = _toHumanizedGroupBy.bind(null, options);
  const toStringFromTo = _toHumanizedFromTo.bind(null, options);
  return toStringFactory({
    toStringV1: criteria => (v1 ? v1(criteria, toStringV1) : toStringV1(criteria)),
    toStringOp: criteria => (op ? op(criteria, toStringOp) : toStringOp(criteria)),
    toStringV2: criteria => (v2 ? v2(criteria, toStringV2) : toStringV2(criteria)),
    toStringUnion,
    toStringOrderBy,
    toStringGroupBy,
    toStringFromTo,
  })(filter);
}

function _toHumanizedV1({ fields = {}, LOCALE = LOCALE_ORIGINAL } = {}, { v1, op, v2 }) {
  if (!fields) {
    return v1;
  }
  return fields[v1] || v1;
}

function _toHumanizedOp({ fields = {}, LOCALE = LOCALE_ORIGINAL } = {}, { v1, op, v2 }) {
  const converter = {
    LIKE: () => {
      const start = v2.startsWith('%');
      const end = v2.endsWith('%');
      if (start && end) {
        return LOCALE.contains;
      }
      return start ? LOCALE.endsWith : LOCALE.startsWith;
    },
    'NOT LIKE': () => {
      const start = v2.startsWith('%');
      const end = v2.endsWith('%');
      if (start && end) {
        return LOCALE.notContains;
      }
      return start ? LOCALE.notEndsWith : LOCALE.notStartsWith;
    },
    IN: () => LOCALE.in,
    'NOT IN': () => LOCALE.notIn,
    IS: () => `${LOCALE.is} ${LOCALE.null}`, // is null
    'IS NOT': () => `${LOCALE.notIs} ${LOCALE.null}`, // is not null
    '=': () => LOCALE.equal,
    '<>': () => LOCALE.notEqual,
    '>': () => LOCALE.greaterThan,
    '<': () => LOCALE.lessThan,
    '>=': () => LOCALE.greaterEqualThan,
    '<=': () => LOCALE.lessEqualThan,
  };
  return converter[op]() || op;
}

function _toHumanizedV2({ fields = {}, LOCALE = LOCALE_ORIGINAL } = {}, { v1, op, v2 }) {
  const type = typeOfV2({ v2 });
  if (type === 'date') {
    return v2.toLocaleString();
  }
  if (['IS', 'IS NOT'].includes(op)) {
    return '';
  } // LOCALE.null esta devuelto con el op
  v2 = v2
    .replace(/\\_/g, '_')
    // .replace(/(^%)|(%$)|(\\)/, '')
    .replace(/^%/, '...')
    .replace(/%$/, '...')
    .replace(/\\/g, '');
  if (['IN', 'NOT IN'].includes(op)) {
    return `${v2
      .split(',')
      .map(s => s.trim())
      .join(', ')}`;
  }

  return `"${v2}"`;
}

function _toHumanizedUnion({ fields = {}, LOCALE = LOCALE_ORIGINAL } = {}, union) {
  const converter = {
    AND: LOCALE.and,
    OR: LOCALE.or,
  };
  return union ? ` ${converter[union] || union} ` : '';
}

function _toHumanizedOrderBy({ fields = {}, LOCALE = LOCALE_ORIGINAL } = {}, value) {
  if (!value) {
    return '';
  }
  return `${LOCALE.orderBy} ${value}`;
}

function _toHumanizedGroupBy({ fields = {}, LOCALE = LOCALE_ORIGINAL } = {}, value) {
  if (!value) {
    return '';
  }
  return `${LOCALE.groupBy} ${value}`;
}

function _toHumanizedFromTo({ fields = {}, LOCALE = LOCALE_ORIGINAL } = {}, { from, to } = {}) {
  if (!from && !to) {
    return '';
  }
  return ` (${from ? `${LOCALE.from}: ${from}` : ''}${from && to ? ' ' : ''}${to ? `${LOCALE.to}: ${to}` : ''})`;
}
